import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { PlaidLinkStableEvent, usePlaidLink } from 'react-plaid-link';
import { Button, Modal, ModalActions, ModalContent, ModalTitle, ModalTitleInfo } from '@c2fo/liquidity';
import { ListCompanyStatusResponse } from '@/enterprise/data/useCompanyStatus';
import useConfirmBankAccountVerification from '@/enterprise/data/useConfirmBankAccountVerification';
import useCreateBankAccountLinkToken from '@/enterprise/data/useCreateBankAccountLinkToken';
import useCreateBankAccountRelinkToken from '@/enterprise/data/useCreateBankAccountRelinkToken';
import useExchangeBankAccountToken from '@/enterprise/data/useExchangeBankAccountToken';
import DoneStep from './DoneStep';
import ErrorStep from './ErrorStep';
import LoadingStep from './LoadingStep';
import { PlaidStep } from './types';

interface PlaidModalProps {
  open: boolean;
  companyUuid: string;
  onClose: () => void;
  onSuccess?: () => void;
  // Fired once the account actually reaches 'active', in either mode — distinct from `onSuccess`,
  // which only reports the user dismissing the confirmation.
  onConnected?: () => void;
  // Reports whether we're fetching a link token, so the button that opened this modal can show
  // its own loading state instead of us popping up a modal with a loader of our own.
  onLoadingChange?: (loading: boolean) => void;
  // 'relink' resumes Same-Day Micro-deposits verification for an already-connected, pending
  // account via a Plaid update-mode link_token, instead of connecting a brand new account.
  // Requires `bankAccountUuid`. Plaid sends no webhook for this flow — Link's own onSuccess is
  // the only completion signal, client-side — so this mode calls confirmVerification afterward
  // instead of the 'connect' mode's exchange call.
  mode?: 'connect' | 'relink';
  bankAccountUuid?: string;
}

const PlaidModal = ({
  open,
  companyUuid,
  onClose,
  onSuccess,
  onConnected,
  onLoadingChange,
  mode = 'connect',
  bankAccountUuid,
}: PlaidModalProps) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<PlaidStep>('linking');
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const createLinkToken = useCreateBankAccountLinkToken();
  const createRelinkToken = useCreateBankAccountRelinkToken();
  const exchangeToken = useExchangeBankAccountToken();
  const confirmVerification = useConfirmBankAccountVerification();

  const goTo = (target: PlaidStep) => setStep(target);

  const handleClose = () => {
    onClose();
  };

  // Checks the verification outcome with the backend (there's no webhook to push it to us) and
  // shows the result. Called from both onSuccess (code accepted) and onExit (user closed Link
  // without finishing) — either way, we want to know the account's actual current status rather
  // than assume anything from how Link closed.
  const handleRelinkClose = () => {
    if (!bankAccountUuid) {
      goTo('error');
      return;
    }

    goTo('confirming');
    confirmVerification.mutate(bankAccountUuid, {
      onSuccess: (response) => {
        const status = response.data.status;
        if (status === 'active') {
          onConnected?.();
          goTo('done');
        } else if (status === 'error') {
          goTo('error');
        } else {
          goTo('pending');
        }
      },
      onError: () => goTo('error'),
    });
  };

  const { open: openPlaidLink, ready: plaidLinkReady } = usePlaidLink({
    token: linkToken,
    onSuccess: (publicToken) => {
      if (mode === 'relink') {
        handleRelinkClose();
        return;
      }

      goTo('exchanging');
      exchangeToken.mutate(
        { companyUuid, publicToken },
        {
          onSuccess: async () => {
            // The mutation's own onSuccess already invalidated companyStatus, but doesn't await the
            // refetch — do that ourselves so we can read the fresh value before deciding done vs
            // connection-pending (the exchange response itself carries no verification-outcome signal).
            await queryClient.invalidateQueries({ queryKey: ['companyStatus'] });
            const status = queryClient.getQueryData<ListCompanyStatusResponse>(['companyStatus']);
            const requiresMicrodepositVerification = status?.data?.some(
              (company) =>
                company.companyUuid === companyUuid && company.bankAccount?.status === 'pending_manual_verification'
            );
            if (!requiresMicrodepositVerification) {
              onConnected?.();
            }

            goTo(requiresMicrodepositVerification ? 'connection-pending' : 'done');
          },
          onError: () => goTo('error'),
        }
      );
    },
    onExit: () => {
      if (mode === 'relink') {
        handleRelinkClose();
        return;
      }

      handleClose();
    },
    // Plaid's OPEN event confirms it's actually showing its own UI — that's our cue to stop the
    // triggering button's loading state (the modal is already hidden throughout the 'linking' step).
    onEvent: (eventName) => {
      if (eventName === PlaidLinkStableEvent.OPEN) {
        onLoadingChange?.(false);
      }
    },
  });

  useEffect(() => {
    if (linkToken && plaidLinkReady) {
      openPlaidLink();
    }
  }, [linkToken, plaidLinkReady, openPlaidLink]);

  const handleConnect = () => {
    goTo('linking');
    onLoadingChange?.(true);

    if (mode === 'relink') {
      if (!bankAccountUuid) {
        onLoadingChange?.(false);
        goTo('error');
        return;
      }

      createRelinkToken.mutate(bankAccountUuid, {
        onSuccess: (response) => setLinkToken(response.data.linkToken),
        onError: () => {
          onLoadingChange?.(false);
          goTo('error');
        },
      });
      return;
    }

    createLinkToken.mutate(
      { companyUuid },
      {
        onSuccess: (response) => setLinkToken(response.data.linkToken),
        onError: () => {
          onLoadingChange?.(false);
          goTo('error');
        },
      }
    );
  };

  // Skip our own intro screen entirely — go straight into Plaid Link as soon as the modal is asked to open.
  useEffect(() => {
    if (open) {
      handleConnect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when `open` toggles, not on every handleConnect identity change
  }, [open]);

  useEffect(() => {
    if (!open) {
      onLoadingChange?.(false);
      const timer = setTimeout(() => {
        setStep('linking');
        setLinkToken(null);
      }, 300);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when `open` toggles
  }, [open]);

  // The link-token fetch (and Plaid's own boot/interaction) shows on the triggering button instead
  // (via onLoadingChange), so we never pop our own modal up during 'linking' — only the Plaid-side
  // exchange still needs one. This also keeps Radix from marking Plaid's iframe inert while it's up.
  const modalVisible = open && step !== 'linking';

  const isBusy = step === 'exchanging' || step === 'confirming';

  return (
    <Modal open={modalVisible} onClose={isBusy ? undefined : handleClose} size="sm" disableOutsideClickClose>
      {step === 'done' ? (
        <ModalTitleInfo>
          {t(mode === 'relink' ? 'plaid.modal.relinkDone.title' : 'plaid.modal.done.title')}
        </ModalTitleInfo>
      ) : step === 'connection-pending' ? (
        <ModalTitleInfo>{t('plaid.modal.connectionPending.title')}</ModalTitleInfo>
      ) : step === 'exchanging' ? (
        <ModalTitleInfo>{t('plaid.modal.exchanging.title')}</ModalTitleInfo>
      ) : step === 'confirming' ? (
        <ModalTitleInfo>{t('plaid.modal.confirming.title')}</ModalTitleInfo>
      ) : step === 'pending' ? (
        <ModalTitleInfo>{t('plaid.modal.pending.title')}</ModalTitleInfo>
      ) : (
        <ModalTitle>{t('plaid.modal.intro.title')}</ModalTitle>
      )}

      <ModalContent>
        {step === 'exchanging' && <LoadingStep message={t('plaid.modal.exchanging.message')} />}
        {step === 'confirming' && <LoadingStep message={t('plaid.modal.confirming.message')} />}
        {step === 'error' && <ErrorStep />}
        {step === 'done' && <DoneStep body={mode === 'relink' ? t('plaid.modal.relinkDone.body') : undefined} />}
        {step === 'connection-pending' && <DoneStep body={t('plaid.modal.connectionPending.body')} />}
        {step === 'pending' && <DoneStep body={t('plaid.modal.pending.body')} />}
      </ModalContent>

      {!isBusy && (
        <ModalActions>
          {step === 'error' && (
            <Button variant="primary" onClick={handleConnect}>
              {t('plaid.modal.error.retry')}
            </Button>
          )}
          {step === 'done' && (
            <Button
              variant="primary"
              onClick={() => {
                onSuccess?.();
                handleClose();
              }}
            >
              {t('plaid.modal.action.done')}
            </Button>
          )}
          {step === 'pending' && (
            <Button variant="primary" onClick={handleClose}>
              {t('plaid.modal.action.close')}
            </Button>
          )}
          {step === 'connection-pending' && (
            <Button
              variant="primary"
              onClick={() => {
                onSuccess?.();
                handleClose();
              }}
            >
              {t('plaid.modal.action.done')}
            </Button>
          )}
        </ModalActions>
      )}
    </Modal>
  );
};

export default PlaidModal;
